import { Alert } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDeploymentMode } from "../../hooks/useDeploymentMode";
import { usePackageLimits } from "../../hooks/usePackageLimits";

/** Which BEAS resource a limit belongs to: own LLM lifts "llm", own OpenSearch "store", both "count". */
export type PackageLimitGroup = "llm" | "store" | "count";

/**
 * Compact info strip showing the current limit for the page. With {@code group}, explains when a limit
 * applies (only with the BEAS LLM / BEAS store) and how to lift it, or that a custom quota is in place.
 */
export default function PackageLimitHint({
  children,
  group,
  capped,
}: {
  children: React.ReactNode;
  group?: PackageLimitGroup;
  /** The limit shown in {@code children} is a real cap (not unlimited). */
  capped?: boolean;
}) {
  const { onPrem } = useDeploymentMode();
  const { t } = useTranslation();
  const limits = usePackageLimits();
  if (!children || onPrem) return null;
  let extra: string | null = null;
  if (group && limits && capped) {
    if (limits.packageType === "custom") {
      extra = limits.customUntil
        ? t("packageLimits.customUntil", { date: new Date(limits.customUntil).toLocaleDateString() })
        : t("packageLimits.custom");
    } else if (limits.packageType === "free") {
      extra = t(`packageLimits.byoHint.${group}`);
    }
  }
  return (
    <Alert severity="info" sx={{ py: 0.35, "& .MuiAlert-message": { py: 0.25 } }}>
      {children}
      {extra && <> · {extra}</>}
    </Alert>
  );
}
